import { useEffect, useState } from 'react';
import { useScreenSize, type ScreenSize } from '@/ui/hooks/useScreenSize';

import './style.css';
import { translate } from '@/domain/utils/translate';
import Icon from '@/ui/assets/Icon';
import { FloatingButtonTypes, FLOATING_BUTTON_CONFIGS } from '@/domain/interfaces/IFloatingButtons';
import FloatingButtonModal from '@/ui/components/modals/FloatingButtonModal';


/** Interfaz para las propiedades del componente */
interface ButtonScrollTopProps {
  type?: FloatingButtonTypes;
  onClick?: () => void;
  iconName?: string;
  className?: string;
  showOnlyOnScroll?: boolean;
  /** Posición del botón (1, 2, 3, etc.) para cuando hay múltiples botones */
  position?: number;
}

/**Botón flotante que puede tener diferentes comportamientos según su tipo */
const ButtonScrollTop = ({
  type = FloatingButtonTypes.SCROLL_TOP,
  onClick,
  iconName,
  className = '',
  showOnlyOnScroll = true,
  position,
}: ButtonScrollTopProps) => {
  /** Estado para manejar la visibilidad del botón */
  const [isVisible, setIsVisible] = useState<boolean>(!showOnlyOnScroll);
  /** Estado para manejar la visibilidad del modal */
  const [showModal, setShowModal] = useState<boolean>(false);

  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();
  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = width < maxLargeWidth;

  /** Función que verifica la posición del scroll y actualiza la visibilidad del botón */
  const toggleVisibility = () => {
    if (showOnlyOnScroll) {
      setIsVisible(window.scrollY > 100);
    }
  };

  /** Función para abrir el modal */
  const handleOpenModal = () => {
    setShowModal(true);
  };

  /** Función para cerrar el modal */
  const handleCloseModal = () => {
    setShowModal(false);
  };

  /** Efecto que añade un listener al evento de scroll para controlar la visibilidad del botón */
  useEffect(() => {
    if (showOnlyOnScroll) {
      window.addEventListener('scroll', toggleVisibility);
      /** Verificación inicial de visibilidad */
      toggleVisibility();
    }

    /** Limpieza del listener cuando el componente se desmonta */
    return () => {
      if (showOnlyOnScroll) {
        window.removeEventListener('scroll', toggleVisibility);
      }
    };
  }, [showOnlyOnScroll]);

  /** Obtener la configuración del botón */
  const config = FLOATING_BUTTON_CONFIGS[type];

  /** Configurar el botón con valores personalizados o predeterminados */
  const buttonProps = {
    icon: iconName ?? config.defaultIcon,
    action: onClick ?? (type === FloatingButtonTypes.CONTACT ? handleOpenModal : config.defaultAction),
    styleClass: config.styleClass ?? ''
  };

  /** Determinar si es un botón de contacto */
  const isContactButton = type === FloatingButtonTypes.CONTACT;

  /** Construir las clases del botón según el dispositivo */
  const mobilePositionClass = position && isMobile ? `mobile-position-${position}` : '';
  const positionClass = `floating-button-position-${position ?? 1}`;
  const contactClass = isContactButton ? 'floating-button-contact' : '';

  const CONTACT_US_BUTTON_TEXT: string = translate('modals.contact_us_button_text');

  /** Construir la clase del botón */
  const buttonClass = [
    'floating-button-base',
    positionClass,
    mobilePositionClass,
    contactClass,
    buttonProps.styleClass,
    className
  ].filter(Boolean).join(' ');

  return (
    <>
      {isVisible && (
        <button
          onClick={buttonProps.action}
          className={buttonClass}
        >
          <Icon name={buttonProps.icon} />
          {isContactButton && (
            <span className="contact-text fw-bold">{CONTACT_US_BUTTON_TEXT}</span>
          )}
        </button>
      )}

      {isContactButton && (
        <FloatingButtonModal
          showModal={showModal}
          handleClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default ButtonScrollTop;
